import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MessageThread — buyer ⇄ seller (or admin read-only) conversation view.
 *
 * Props:
 *   conversationId  string   required
 *   readOnly        bool     defaults to false (true for admin viewer)
 *   onClose         function optional close handler for modal use
 */
export default function MessageThread({ conversationId, readOnly = false, onClose }) {
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const path = isAdmin
        ? `/admin/conversations/${conversationId}/messages`
        : `/conversations/${conversationId}/messages`;
      const { data } = await api.get(path);
      setConv(data.conversation);
      setMessages(data.messages);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => {
    if (!conversationId) return;
    load();
    const interval = setInterval(load, 12000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/messages`, { body });
      setMessages((m) => [...m, data]);
      setDraft("");
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!conv) return <div className="brut-border bg-white p-8 mono text-xs uppercase tracking-[0.2em] text-[#555]">Loading conversation…</div>;

  return (
    <div className="brut-border bg-white" data-testid={`thread-${conversationId}`}>
      <div className="flex items-center justify-between border-b-2 border-[#1F3D2A] px-5 py-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Conversation</div>
          <div className="heading font-bold mt-1">{conv.seller_name || conv.seller_email}</div>
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">Order {conv.order_id.slice(-10)}</div>
        </div>
        {onClose && <button onClick={onClose} className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white">Close ✕</button>}
      </div>

      <div ref={scrollRef} className="p-5 h-96 overflow-y-auto space-y-3 bg-[#FDF3E9]">
        {messages.length === 0 ? (
          <div className="text-center mono text-[10px] uppercase tracking-[0.25em] text-[#555] mt-16">
            No messages yet. {readOnly ? "" : "Start the conversation below."}
          </div>
        ) : (
          messages.map((m) => {
            const mine = !readOnly && m.sender_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] brut-border p-3 ${mine ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                  <div className={`mono text-[10px] uppercase tracking-[0.25em] ${mine ? "text-white/60" : "text-[#C77E3C]"}`}>
                    {m.sender_role === "buyer" ? "Buyer" : "Seller"} · {m.sender_name}
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{m.body}</div>
                  <div className={`mono text-[9px] mt-2 ${mine ? "text-white/50" : "text-[#999]"}`}>{new Date(m.at).toLocaleString()}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!readOnly && (
        <form onSubmit={send} className="border-t-2 border-[#1F3D2A] p-3 flex gap-2 bg-white">
          <input data-testid={`msg-input-${conversationId}`} value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="Type a message about the pickup…"
            className="flex-1 brut-border bg-white px-3 py-2 text-sm" />
          <button data-testid={`msg-send-${conversationId}`} disabled={sending || !draft.trim()} className="brut-border bg-[#C77E3C] text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A] disabled:opacity-40">
            {sending ? "…" : "Send"}
          </button>
        </form>
      )}
      {readOnly && (
        <div className="border-t-2 border-[#1F3D2A] px-5 py-2 bg-[#EDE5D2] mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
          Admin read-only view
        </div>
      )}
    </div>
  );
}
